import * as Linking from 'expo-linking';
import React, { useCallback } from 'react';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import styled from 'styled-components/native';

import { Color } from '~/components/color';
import { Text } from '~/components/text';
import { FormattedMessage } from '~/contexts/intl';
import { FALLBACK_MORE_INFO_URL, isHttpUrl, useServiceNotice } from '~/contexts/service-notice';

const ScreenContainer = styled(View)`
    flex: 1;
    width: 100%;
    background-color: ${Color.BACKGROUND_DEFAULT};
`;

const Content = styled.View<{ topInset: number; bottomInset: number }>`
    flex: 1;
    padding-top: ${({ topInset }) => 24 + topInset}px;
    padding-bottom: ${({ bottomInset }) => 24 + bottomInset}px;
    padding-horizontal: 32px;
    align-items: center;
    justify-content: center;
`;

const Link = styled(Text.SMALL_BUTTON_LABEL)`
    text-decoration: underline;
`;

/**
 * Fully static screen shown when the service notice validity has passed and the
 * service is no longer available. Rendered instead of the whole app (outside any
 * navigator), so it must not use navigation hooks or start any network traffic.
 */
export function ServiceUnavailableScreen() {
    const { notice } = useServiceNotice();
    const { top, bottom } = useSafeAreaInsets();

    // The stored notice carries the link with more information. When there is no stored notice
    // at all (e.g. the app was reinstalled after the backend was switched off), fall back to the
    // link built into the app. A stored notice without a link is respected as "no link": that is
    // a deliberate choice in the backend configuration. Only http(s) links are ever shown or
    // opened (the URL is server-controlled, but this keeps an unexpected scheme from reaching
    // the linking APIs).
    const moreInfoUrl = notice ? notice.moreInfoUrl : FALLBACK_MORE_INFO_URL;
    const canShowMoreInfoUrl = isHttpUrl(moreInfoUrl);

    const handleOpenMoreInfoUrl = useCallback(async () => {
        if (isHttpUrl(moreInfoUrl) && (await Linking.canOpenURL(moreInfoUrl))) {
            await Linking.openURL(moreInfoUrl);
        }
    }, [moreInfoUrl]);

    return (
        <ScreenContainer>
            <Content topInset={top} bottomInset={bottom}>
                <Text.HEADER_2 before={24} style={{ textAlign: 'center' }}>
                    <FormattedMessage id="service-unavailable.title" />
                </Text.HEADER_2>
                <Text.PARAGRAPH_1 before={24} after={24} style={{ textAlign: 'center' }}>
                    <FormattedMessage id="service-unavailable.description" />
                </Text.PARAGRAPH_1>
                {canShowMoreInfoUrl ? (
                    <Link onPress={handleOpenMoreInfoUrl}>
                        <FormattedMessage id="service-notice.read-more" />
                    </Link>
                ) : null}
            </Content>
        </ScreenContainer>
    );
}
